import { formatGradeLabel } from "@/lib/catalog/cardIdentity";

/**
 * What a unit of stock is: **Raw**, **Graded** or **Sealed** (`CONTEXT.md`, TRO-1101).
 *
 * The one split every add flow and filter offers: hand-add, the inventory filter, and the
 * storefront browse filter. It mirrors the server's `StockKind` (`backend/src/trovie/domain/enums.py`)
 * and is derived, never stored: a slab is `is_graded`, a box is a `sealed` Product, and Raw is the
 * rest. An ungraded box is Sealed, never Raw.
 */
export type Kind = "raw" | "graded" | "sealed";

export const KINDS: readonly { kind: Kind; label: string; }[] = [
  { kind: "raw", label: "Raw" },
  { kind: "graded", label: "Graded" },
  { kind: "sealed", label: "Sealed" },
];

/** The kind's name as every screen shows it ("Raw", "Graded", "Sealed"). */
export function kindLabel(kind: Kind): string {
  return KINDS.find((k) => k.kind === kind)?.label ?? kind;
}

export function isKind(value: unknown): value is Kind {
  return value === "raw" || value === "graded" || value === "sealed";
}

/** The kind of a unit, read off the two facts every stock row carries. */
export function kindOf(item: { is_graded?: boolean | null; product_type?: string | null; }): Kind {
  if (item.is_graded) return "graded";
  return item.product_type === "sealed" ? "sealed" : "raw";
}

/** The single pill shown for a stock row's condition, grade, or sealed form. */
export function inventoryKindPill(item: {
  is_graded?: boolean | null;
  product_type?: string | null;
  condition?: string | null;
  grading_company?: string | null;
  grade?: string | null;
}): { kind: Kind; label: string | null; } {
  const kind = kindOf(item);
  if (kind === "graded") {
    return { kind, label: formatGradeLabel(item.grading_company, item.grade, true) };
  }
  return { kind, label: kind === "sealed" ? "Sealed" : item.condition ?? null };
}
